/**
 * Draft Rescue — Export & Import for the Bulk Photo Sorter.
 *
 * Why this exists:
 *   A wedged / OOM'd draft on an iPhone can hold 400+ photos hostage inside
 *   Mobile Safari's IndexedDB. This module lets the user download the whole
 *   draft as a ZIP (photos + a manifest.json describing clusters and SKU
 *   picks) on the phone, then re-import it on a Mac to finish sorting on a
 *   machine that isn't one bad canvas decode away from crashing.
 *
 * ZIP layout:
 *   manifest.json                          — see MANIFEST_VERSION
 *   photos/unassigned/<id>__<name>         — raw file blobs, one per photo
 *   photos/cluster-01/<id>__<name>
 *   photos/cluster-02/<id>__<name>
 *   ...
 *
 * Manifest shape (v1):
 *   {
 *     version: 1,
 *     exportedAt: <ms>,
 *     gap: <seconds>,
 *     clusters: [
 *       { id, targetItemId, photos: [{ id, name, size, takenAt, path }] }
 *     ],
 *     unassigned: [{ id, name, size, takenAt, path }],
 *   }
 *
 * `path` is the in-ZIP location of the blob. We keep a `path` per photo
 * (rather than deriving it) so future manifest versions can rename folders
 * without breaking older exports.
 */

import JSZip from "jszip";

export const MANIFEST_VERSION = 1;

// Sanitise a filename component so it survives Windows/macOS/ZIP tooling.
// (Long filenames, colons from ':', slashes, control chars.)
const safeName = (name) =>
  (name || "photo")
    .replace(/[\\/:*?"<>|\x00-\x1f]/g, "_")
    .slice(-120); // Cap length; some filesystems max at 255 but ZIP paths are longer.

const clusterFolder = (idx) => `photos/cluster-${String(idx + 1).padStart(2, "0")}`;
const UNASSIGNED_FOLDER = "photos/unassigned";

// -------------------------------------------------------------------- export
/**
 * Build a downloadable ZIP Blob from the current sorter state.
 *
 * @param {object} state
 * @param {Array}  state.clusters     Current clusters (each with .photos, .targetItemId, .savedAt)
 * @param {Array}  state.unassigned   Photos not attached to a cluster
 * @param {number} state.gap          Current clustering-gap seconds (persisted for round-trip)
 * @param {(pct:number)=>void} [onProgress]  0-100 progress callback for the UI.
 * @returns {Promise<Blob>}
 */
export async function exportDraftZip({ clusters, unassigned, gap }, onProgress) {
  const zip = new JSZip();

  // Skip clusters that were already persisted server-side — those are
  // the user's committed work, not part of the "rescue this draft" scope.
  const draftClusters = (clusters || []).filter((c) => !c.savedAt);

  const manifest = {
    version: MANIFEST_VERSION,
    exportedAt: Date.now(),
    gap: typeof gap === "number" ? gap : null,
    clusters: [],
    unassigned: [],
  };

  draftClusters.forEach((c, idx) => {
    const folder = clusterFolder(idx);
    const clusterEntry = {
      id: c.id,
      targetItemId: c.targetItemId || null,
      photos: [],
    };
    c.photos.forEach((p) => {
      const path = `${folder}/${p.id}__${safeName(p.name)}`;
      zip.file(path, p.file, { binary: true });
      clusterEntry.photos.push({
        id: p.id,
        name: p.name,
        size: p.size,
        takenAt: p.takenAt || null,
        path,
      });
    });
    manifest.clusters.push(clusterEntry);
  });

  (unassigned || []).forEach((p) => {
    const path = `${UNASSIGNED_FOLDER}/${p.id}__${safeName(p.name)}`;
    zip.file(path, p.file, { binary: true });
    manifest.unassigned.push({
      id: p.id,
      name: p.name,
      size: p.size,
      takenAt: p.takenAt || null,
      path,
    });
  });

  zip.file("manifest.json", JSON.stringify(manifest, null, 2));

  // STORE (no compression) — photos are already JPEG/HEIC-decoded JPEGs, so
  // deflate spends CPU for ~1-2% gain. On mobile the CPU savings matter more
  // than the byte savings; a 458-photo, ~2GB draft would otherwise take
  // 5+ minutes to compress on an iPhone.
  return zip.generateAsync(
    { type: "blob", compression: "STORE" },
    (meta) => {
      if (onProgress && typeof meta.percent === "number") onProgress(meta.percent);
    },
  );
}

/**
 * Trigger a browser download of a ZIP blob.
 * Isolated so it can be swapped for a Web Share API path on iOS if needed.
 */
export function downloadZipBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after a tick so Safari has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

// -------------------------------------------------------------------- import
/**
 * Parse a rescue ZIP back into sorter state.
 *
 * @param {Blob|File} zipFile
 * @param {(pct:number, msg?:string)=>void} [onProgress]
 * @returns {Promise<{ clusters:Array, unassigned:Array, gap:number|null }>}
 *
 * Photo entries in the returned state carry a fresh `previewUrl` (blob: URL
 * created here) so the caller can render them immediately. Photo `id`s are
 * re-generated to avoid collisions with anything already open in the sorter.
 */
export async function importDraftZip(zipFile, onProgress) {
  const zip = await JSZip.loadAsync(zipFile);
  const manifestEntry = zip.file("manifest.json");
  if (!manifestEntry) {
    throw new Error("this ZIP doesn't look like a maison pilet draft — no manifest.json inside.");
  }
  const manifestText = await manifestEntry.async("string");
  let manifest;
  try {
    manifest = JSON.parse(manifestText);
  } catch {
    throw new Error("manifest.json is corrupted — the ZIP may be truncated.");
  }
  if (!manifest || manifest.version !== MANIFEST_VERSION) {
    throw new Error(`unsupported draft version ${manifest?.version ?? "?"} — expected ${MANIFEST_VERSION}.`);
  }

  // Re-key photos on import so re-importing the same ZIP twice doesn't
  // collide. React keys need to be unique per session.
  let uid = 0;
  const nextId = () => `p_${Date.now()}_r${uid++}`;

  const totalPhotos =
    (manifest.clusters || []).reduce((n, c) => n + (c.photos?.length || 0), 0) +
    (manifest.unassigned || []).length;
  let done = 0;
  const bump = () => {
    done += 1;
    if (onProgress && totalPhotos > 0) {
      onProgress(Math.round((done / totalPhotos) * 100));
    }
  };

  const readPhoto = async (entry) => {
    const file = zip.file(entry.path);
    if (!file) throw new Error(`missing photo blob at ${entry.path}`);
    // `blob` here is a real Blob (which the sorter treats as a File). The
    // save-all upload path uses FormData which accepts either; we wrap it
    // in a File so downstream code that reads .name doesn't NPE.
    const blob = await file.async("blob");
    const asFile = new File([blob], entry.name || "photo.jpg", {
      type: blob.type || "image/jpeg",
    });
    bump();
    return {
      id: nextId(),
      file: asFile,
      name: asFile.name,
      size: asFile.size,
      takenAt: entry.takenAt || null,
      previewUrl: URL.createObjectURL(asFile),
    };
  };

  const clusters = [];
  for (const c of (manifest.clusters || [])) {
    const photos = [];
    for (const entry of (c.photos || [])) {
      photos.push(await readPhoto(entry));
    }
    clusters.push({
      id: `c_${Date.now()}_${clusters.length}`, // fresh cluster id too
      targetItemId: c.targetItemId || null,
      savedAt: null,
      photos,
    });
  }

  const unassigned = [];
  for (const entry of (manifest.unassigned || [])) {
    unassigned.push(await readPhoto(entry));
  }

  return {
    clusters,
    unassigned,
    gap: typeof manifest.gap === "number" ? manifest.gap : null,
  };
}
